import { BCButtonOverflow, findMessage, IButtonOverflowItem } from '@bimser/components';
import { FormItemTypes, IDictionary, MLHelper } from '@bimser/core';
import { GetIconComponent } from '@bimser/icons';
import GetIcon16ByName from '@bimser/icons/lib/getIcon16ByName';
import { JSX } from 'react';
import { IUserEvent } from '../../../../entities/services/IUserEvent';
import ContainerControl from '../../base/ContainerControl';
import { IRibbonItemClickEventArgs } from '../../entities/EventArgs';
import Styles from './assets/style.scss';
import { EToolbarItemType, IRibbonItem } from './entities';
import { IRibbonProperties } from './entities/IRibbonProperties';

const overflowButtonProps = { type: 'text' as const };
const dropdownProps = {
	placement: 'bottomLeft' as const
};

export default class Ribbon extends ContainerControl {
	ribbonItems: IRibbonItem[];

	constructor() {
		super();
		this.controlType = FormItemTypes.EControlTypes.Ribbon;
		this.ribbonItems = [
			{
				key: 'New',
				title: findMessage.getAsMultiLanguageObject('100657'),
				type: EToolbarItemType.Button,
				icon: 'add',
				items: [
					{
						key: 'NewSolution',
						title: findMessage.getAsMultiLanguageObject('100035'),
						type: EToolbarItemType.Button,
						icon: 'new-solution'
					},
					{
						key: 'NewProject',
						title: findMessage.getAsMultiLanguageObject('100036'),
						type: EToolbarItemType.Button,
						icon: 'new-project'
					}
				]
			},
			{
				key: 'Open',
				title: findMessage.getAsMultiLanguageObject('100014'),
				type: EToolbarItemType.Button,
				icon: 'open',
				items: [
					{
						key: 'Save',
						title: findMessage.getAsMultiLanguageObject('100620'),
						type: EToolbarItemType.Button,
						icon: 'open-solution',
						orderNo: 0
					},
					{
						key: 'Project',
						title: findMessage.getAsMultiLanguageObject('100038'),
						type: EToolbarItemType.Button,
						icon: 'open-project',
						orderNo: 0
					}
				]
			},
			{
				key: 'Divider1',
				type: EToolbarItemType.Divider,
				title: findMessage.getAsMultiLanguageObject('100793'),
				orderNo: 1
			},
			{
				key: 'Undo',
				title: null,
				type: EToolbarItemType.Button,
				icon: 'undo',
				items: [
					{
						key: 'Undo',
						title: findMessage.getAsMultiLanguageObject('100691'),
						type: EToolbarItemType.Button,
						icon: 'undo'
					},
					{
						key: 'Redo',
						title: findMessage.getAsMultiLanguageObject('100692'),
						type: EToolbarItemType.Button,
						icon: 'redo'
					}
				]
			},
			{
				key: 'Clipboard',
				title: null,
				type: EToolbarItemType.Button,
				icon: 'clipboard',
				items: [
					{
						key: 'Copy',
						title: findMessage.getAsMultiLanguageObject('100012'),
						type: EToolbarItemType.Button,
						icon: 'copy'
					},
					{
						key: 'Paste',
						title: findMessage.getAsMultiLanguageObject('100693'),
						type: EToolbarItemType.Button,
						icon: 'paste'
					}
				]
			},
			{
				key: 'Divider2',
				type: EToolbarItemType.Divider,
				title: findMessage.getAsMultiLanguageObject('100793'),
				orderNo: 1
			},
			{
				key: 'ImportExport',
				title: findMessage.getAsMultiLanguageObject('101858'),
				type: EToolbarItemType.Button,
				icon: 'import-export',
				items: [
					{
						key: 'Export',
						title: findMessage.getAsMultiLanguageObject('100288'),
						type: EToolbarItemType.Button,
						icon: 'export'
					},
					{
						key: 'Import',
						title: findMessage.getAsMultiLanguageObject('100690'),
						type: EToolbarItemType.Button,
						icon: 'import'
					}
				]
			}
		];

		this.caption.visible = false;
		this.onRibbonItemClick = this.onRibbonItemClick.bind(this);
		this.getCommonItemProps = this.getCommonItemProps.bind(this);
	}

	__loadProperties(data: IRibbonProperties) {
		super.__loadProperties(data);
		this.__proxyChanged.ribbonItems = data.ribbonItems;
	}

	private getCommonItemProps(item: IRibbonItem) {
		return {
			title: MLHelper.getMLText(item.title),
			icon: item.icon ? GetIcon16ByName(item.icon).info : null,
			disabled: item.disabled || this.readOnly || !this.enabled || !this.clientEnabled
		};
	}

	private onRibbonItemClick(item: IRibbonItem) {
		const argument: IRibbonItemClickEventArgs = {
			item
		};

		this.emit<IRibbonItemClickEventArgs>('event:OnRibbonItemClick', argument);
		this.emit<IUserEvent<IRibbonItemClickEventArgs>>('event:server:OnRibbonItemClick', {
			argument,
			controlId: this.name,
			name: 'RibbonItemClick',
			sender: 'web'
		});
	}

	private getToolbarItems(items: Array<IRibbonItem>): IButtonOverflowItem[] {
		return items?.map(i => {
			if (i.type === EToolbarItemType.Divider) {
				return {
					key: i.key,
					type: 'divider'
				};
			}

			const item: IButtonOverflowItem = {
				key: i.key
			};

			const { title, icon, disabled } = this.getCommonItemProps(i);
			item.buttonProps = {
				children: title,
				title: title,
				icon: GetIconComponent(icon),
				type: 'text',
				disabled,
				onClick: () => this.onRibbonItemClick(i)
			};

			if (i.items?.length) {
				item.items = this.getToolbarItems(i.items);
			}
			if (i.shortCut) {
				item.dropdownMenuItemProps = {
					extra: i.shortCut
				};
			}

			return item;
		});
	}

	__getComponentData(): IDictionary<any> {
		return {
			...super.__getComponentData(),
			ribbonItems: this.ribbonItems
		};
	}

	__render(children?: JSX.Element) {
		return super.__render(
			<div className={Styles.ribbonContainer}>
				<BCButtonOverflow
					items={this.getToolbarItems(this.ribbonItems)}
					overflowButtonProps={overflowButtonProps}
					style={{ backgroundColor: this.style.backgroundColor }}
					dropdownProps={dropdownProps}
				/>
			</div>
		);
	}
}
